import React, { useEffect } from 'react';
import Brand from '../components/Brand';

/**
 * ModeSelector — Inkwell editorial mode picker.
 * Three numbered tiles (01/02/03), large hanzi glyphs, vermillion accent on Battle.
 */
function ModeSelector({ onSelectMode, wordCount = 0 }) {
  const modes = [
    {
      key: 'classic',
      num: '01',
      han: '学',
      title: 'Study',
      desc: 'Spaced-repetition flashcards. Calm and focused.',
      battle: false,
    },
    {
      key: 'paragraph',
      num: '02',
      han: '读',
      title: 'Reading',
      desc: 'Read a passage, then translate from understanding.',
      battle: false,
    },
    {
      key: 'quiz-battle',
      num: '03',
      han: '战',
      title: 'Battle',
      desc: 'Multiplayer quiz. Race friends to answer.',
      battle: true,
    },
  ];

  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= modes.length) {
        onSelectMode(modes[num - 1].key);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelectMode]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--paper)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header className="ink-header">
        <Brand />
        <div style={{ width: 80 }} />
      </header>

      {/* Hero */}
      <div className="ms-hero" style={{ padding: '64px 80px 40px' }}>
        <div className="t-eyebrow" style={{ marginBottom: 16 }}>
          Study modes · 学习模式
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 24, flexWrap: 'wrap' }}>
          <h1
            className="t-display"
            style={{ fontFamily: 'var(--font-han)', fontWeight: 500 }}
          >
            选择一个模式
          </h1>
          <span className="t-body">
            Choose a mode to begin · {wordCount > 0 ? `${wordCount} characters ready` : 'Add your first words to get started'}
          </span>
        </div>
      </div>

      {/* Mode tiles */}
      <div
        className="ms-tiles"
        style={{
          padding: '0 80px 64px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 24,
        }}
      >
        {modes.map((m, i) => (
          <button
            key={m.key}
            onClick={() => onSelectMode(m.key)}
            className="ms-tile"
            style={{
              position: 'relative',
              background: m.battle ? 'var(--battle-bg)' : 'var(--paper)',
              border: m.battle ? '1px solid var(--battle-bg)' : '1px solid var(--rule)',
              borderRadius: 12,
              padding: 32,
              paddingBottom: 28,
              textAlign: 'left',
              cursor: 'pointer',
              color: m.battle ? 'var(--battle-fg)' : 'var(--ink)',
              transition: 'transform .15s, box-shadow .15s',
              minHeight: 320,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              fontFamily: 'var(--font-sans)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--sh3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = '';
            }}
          >
            <div>
              <div style={{ marginBottom: 32 }}>
                <span
                  className="t-mono"
                  style={{
                    fontSize: 12,
                    letterSpacing: '0.14em',
                    opacity: 0.6,
                  }}
                >
                  {m.num}
                </span>
              </div>
              <div
                className="ms-tile-han"
                style={{
                  fontFamily: 'var(--font-han)',
                  fontSize: 96,
                  lineHeight: 0.95,
                  letterSpacing: '0.04em',
                  marginBottom: 24,
                  color: m.battle ? 'var(--paper)' : 'var(--ink)',
                }}
              >
                {m.han}
              </div>
            </div>
            <div>
              <h2 className="t-h2" style={{ marginBottom: 8 }}>
                {m.title}
              </h2>
              <p
                className="t-small"
                style={{
                  color: m.battle ? '#c8c1b0' : 'var(--ink-3)',
                }}
              >
                {m.desc}
              </p>
            </div>
            {m.battle && (
              <div
                style={{
                  position: 'absolute',
                  top: 24,
                  right: 24,
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: 'var(--vermillion)',
                  boxShadow: '0 0 0 4px rgba(216,58,44,0.2)',
                }}
              />
            )}
          </button>
        ))}
      </div>

      <div
        className="ms-footer"
        style={{
          padding: '0 80px 32px',
          marginTop: 'auto',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <span className="t-small">
          {wordCount > 0 ? `${wordCount} words` : 'No words yet'}
        </span>
      </div>
    </div>
  );
}

export default ModeSelector;
